// src/components/SEO.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Minimal SEO component. Pass absolute image URLs for OG.
 * Example usage:
 * <SEO title="..." description="..." canonical="https://..." images={["https://..."]} product={productObj} />
 */
const SEO = ({ title, description, canonical, images = [], product = null, twitterHandle = null }) => {
  const primaryImage = images.length ? images[0] : null;

  const jsonLd = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name || title,
        image: product.images || images,
        description: product.description || description,
        sku: product.sku || undefined,
        brand: product.brand ? { "@type": "Brand", name: product.brand } : undefined,
        offers: product.price
          ? {
              "@type": "Offer",
              url: product.url || canonical || (typeof window !== "undefined" ? window.location.href : undefined),
              price: product.price,
              priceCurrency: product.priceCurrency || "INR",
              availability: product.availability || "http://schema.org/InStock",
            }
          : undefined,
      }
    : null;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={product ? "product" : "website"} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {primaryImage && <meta property="og:image" content={primaryImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {primaryImage && <meta name="twitter:image" content={primaryImage} />}

      {/* JSON-LD Product (if provided) */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default SEO;
